/**
 * filesApi.js
 *
 * The browser side of files_api.php, plus the small helpers both Files pages
 * share.
 *
 * Every access decision is made by the server from the session. Nothing here
 * decides who may see what - it only asks, and shows what comes back. That is
 * deliberate: the generic getDatas API is open to any logged-in client, so a
 * rule enforced only in React would not be a rule.
 */

import axios from "axios";

import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AudioFileOutlinedIcon from "@mui/icons-material/AudioFileOutlined";
import VideoFileOutlinedIcon from "@mui/icons-material/VideoFileOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";

/** Same host AuthContext posts its CRUD calls to. */
export const FILES_URL =
  "https://mindsinaction.com.na/api/mindsbackend/files_api.php";

/**
 * Call one action. Always resolves - never throws - with the endpoint's
 * { success, message, data } shape, so callers have one thing to check.
 *
 * `action` and `token` also ride in the query string. A body larger than
 * PHP's post_max_size arrives with $_POST EMPTY, which would otherwise turn
 * "your file is too big" into a baffling "Unauthorized".
 */
export const filesCall = async (action, fields = {}, files = [], onProgress) => {
  const form = new FormData();
  form.append("action", action);
  form.append("token", process.env.REACT_APP_API_TOKEN);
  Object.entries(fields).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    form.append(k, Array.isArray(v) ? v.join(",") : String(v));
  });
  files.forEach((f) => form.append("files[]", f));

  const qs = new URLSearchParams({
    action,
    token: process.env.REACT_APP_API_TOKEN || "",
  }).toString();

  try {
    const res = await axios.post(`${FILES_URL}?${qs}`, form, {
      withCredentials: true,
      onUploadProgress: onProgress
        ? (e) => onProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0)
        : undefined,
    });
    return res?.data ?? { success: false, message: "No response from the server" };
  } catch (err) {
    // 403/404/409/413 come back with a JSON body that says why - use it.
    const body = err?.response?.data;
    if (body && typeof body === "object") return body;
    return { success: false, message: err?.message || "Request failed" };
  }
};

/**
 * A signed, short-lived URL for one file. dl=true forces a download; without
 * it, types a browser can display are served inline for the preview.
 *
 * The signature authorises the request, not a cookie - which is what lets an
 * <img>, <video> or <iframe> load it from another origin, and lets video seek.
 */
export const fileLink = async (id, dl = false) => {
  const res = await filesCall("link", { id, dl: dl ? 1 : 0 });
  if (!res?.success) return { url: null, message: res?.message };
  return { url: `${FILES_URL}?${res.data.query}`, message: "" };
};

/** Download through the browser's own download manager - no memory blob. */
export const downloadFile = async (id) => {
  const { url, message } = await fileLink(id, true);
  if (!url) return message || "Could not download this file";
  // An attachment response does not navigate away from the page.
  window.location.assign(url);
  return "";
};

/* ------------------------------------------------------------------ */

/** The groups on the folder page, in display order. */
export const KIND_GROUPS = [
  { kind: "document", label: "Documents" },
  { kind: "image", label: "Images" },
  { kind: "audio", label: "Music & audio" },
  { kind: "video", label: "Videos" },
  { kind: "other", label: "Other" },
];

export const fileIcon = (file, sx) => {
  const ext = String(file?.extension || "").toLowerCase();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={sx} />;
  switch (file?.kind) {
    case "document":
      return <DescriptionOutlinedIcon sx={sx} />;
    case "image":
      return <ImageOutlinedIcon sx={sx} />;
    case "audio":
      return <AudioFileOutlinedIcon sx={sx} />;
    case "video":
      return <VideoFileOutlinedIcon sx={sx} />;
    case "other":
      return <FolderZipOutlinedIcon sx={sx} />;
    default:
      return <InsertDriveFileOutlinedIcon sx={sx} />;
  }
};

/** A colour per kind, so the groups read at a glance. */
export const kindColor = (kind) =>
  ({
    document: "#1565c0",
    image: "#2e7d32",
    audio: "#8e24aa",
    video: "#c62828",
    other: "#6d4c41",
  }[kind] || "#607d8b");

export const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1073741824) return `${(b / 1048576).toFixed(1)} MB`;
  return `${(b / 1073741824).toFixed(2)} GB`;
};

/**
 * MySQL hands back "2026-10-08 14:33:00". Safari will not parse the space,
 * so swap in the T before anything calls new Date() on it.
 */
export const isoish = (v) => (v ? String(v).replace(" ", "T") : v);
